<template>
  <div>
    <v-row>
      <h2>グループ設定</h2>
      <v-spacer />
      <v-btn @click="clickCreateBtn" color="primary" variant="flat">
        <v-icon start>mdi-plus</v-icon>
        グループ作成
      </v-btn>
    </v-row>

    <!-- searchFilters -->
    <v-row>
      <v-col cols="3" class="pl-0">
        <v-text-field
          v-model="searchFilters.year"
          suffix="年度"
          label="年度"
          type="number"
          hide-details
          variant="outlined"
          density="compact"
        >
        </v-text-field>
      </v-col>
      <v-col cols="9" align-self="center">
        <div v-if="loading">
          <v-progress-circular
            color="primary"
            indeterminate
          ></v-progress-circular>
        </div>
      </v-col>
    </v-row>

    <!-- group list -->
    <div class="pt-6" v-if="groups.length > 0">
      <v-list>
        <v-list-item v-for="(item, i) in groups" :key="i" class="pa-2">
          <v-card @click="clickItem(item)" variant="outlined" link width="100%" color="grey">
            <v-card-text class="text-black">
              <v-row>
                <v-col cols="6">
                  {{ item.name }}
                </v-col>
                <v-col cols="3">
                  {{ item.year + "年度" }}
                </v-col>
                <v-col cols="3">
                  {{ "作成日: " + formatDate(item.createdAt) }}
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-list-item>
      </v-list>
    </div>

    <div class="pt-6" v-else>
      <h5>グループはありません</h5>
    </div>

    <!-- dialog -->
    <v-dialog v-model="dialog" max-width="620px">
      <v-card>
        <v-card-title>
          {{ isEdit ? "グループ設定" : "グループ作成" }}
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row align="center">
              <v-col cols="6">
                <div>対象年度</div>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="group.year"
                  type="number"
                  suffix="年度"
                  variant="outlined"
                  hide-details=""
                  density="compact"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row align="center">
              <v-col cols="6">
                <div>グループ名</div>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="group.name"
                  variant="outlined"
                  hide-details=""
                  density="compact"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-container>
            <v-row>
              <v-btn @click="dialog = false" color="grey" text>
                キャンセル
              </v-btn>
              <v-spacer />
              <v-btn
                v-if="isEdit"
                @click="deleteGroup()"
                :loading="loading"
                class="mr-2"
                color="error"
                variant="flat"
              >
                削除
              </v-btn>
              <v-btn
                v-if="isEdit"
                @click="updateGroup"
                :loading="loading"
                color="primary"
                variant="flat"
              >
                保存
              </v-btn>
              <v-btn
                v-else
                @click="createGroup"
                :loading="loading"
                color="primary"
                variant="flat"
              >
                作成
              </v-btn>
            </v-row>
          </v-container>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import ApiGroup from "@/api/ApiGroup";
import LocalDataService from "@/service/LocalDataService";
import { groupModule } from "@/store/dataModules/groupModule";
import { snackbarModule } from "@/store/viewModules/snackbarModule";
import CommonUtil from "@/utils/CommonUtil";
import { NurseryGroupListResponse, NurseryGroupResponse } from "chaild-api/lib";
import dayjs from "dayjs";
import { defineComponent } from "vue";

export default defineComponent({
  name: "GroupSetting",

  async mounted() {
    await this.listGroups();
  },

  watch: {
    searchFilters: {
      deep: true,
      handler() {
        this.listGroups();
      },
    },
  },

  data() {
    return {
      loading: false,
      dialog: false,
      isEdit: false,
      searchFilters: {
        year: CommonUtil.getFiscalYear(),
      },
    };
  },

  computed: {
    groups: function (): NurseryGroupListResponse | null {
      return groupModule.groups;
    },
    group: function (): NurseryGroupResponse | null {
      return groupModule.group;
    },
  },

  methods: {
    async listGroups() {
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await groupModule.listGroups({
          nurseryId,
          ...this.searchFilters,
        });
      }
    },
    clickItem(value: NurseryGroupResponse) {
      this.isEdit = true;
      groupModule.setGroup(value);
      this.dialog = true;
    },
    clickCreateBtn() {
      this.isEdit = false;
      groupModule.setGroup({
        year: Number(this.searchFilters.year),
        groupId: 0,
        name: "",
        createdAt: "",
        updatedAt: null,
      });
      this.dialog = true;
    },
    async createGroup() {
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId && groupModule.group) {
        this.loading = true;
        const response = await ApiGroup.createGroup({
          nurseryId,
          year: String(groupModule.group.year),
          name: groupModule.group.name,
        }).catch(() => (this.loading = false));
        if (response) {
          this.dialog = false;
          snackbarModule.snackOn({
            message: "グループを作成しました",
          });
          await this.listGroups();
        }
        this.loading = false;
      }
    },
    async updateGroup() {
      if (groupModule.group) {
        this.loading = true;
        const response = await ApiGroup.updateGroup({
          groupId: groupModule.group.groupId,
          year: String(groupModule.group.year),
          name: groupModule.group.name,
        }).catch(() => (this.loading = false));
        if (response) {
          this.dialog = false;
          snackbarModule.snackOn({
            message: "保存しました",
          });
          await this.listGroups();
        }
        this.loading = false;
      }
    },
    async deleteGroup() {
      if (groupModule.group) {
        const answer = confirm("グループを削除しますか？");
        if (answer) {
          this.loading = true;
          const response = await ApiGroup.deleteGroup({
            groupId: groupModule.group.groupId,
          }).catch(() => (this.loading = false));
          if (response) {
            this.dialog = false;
            snackbarModule.snackOn({
              message: "削除しました",
            });
            await this.listGroups();
          }
          this.loading = false;
        }
      }
    },
    formatDate(value: string) {
      if (!value) {
        return "";
      }
      return dayjs(value).format("YYYY/MM/DD");
    },
  },
});
</script>
